/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import type { AiManagedObject, AiManagedObjectConfig } from '@atlassian/ai-managed-objects/types';
import { validateManagedBlockConfig } from '@atlassian/ai-managed-objects/validation';

import { messages } from '../../common/messages';

import { AimoStatusMetadata } from './AimoStatusMetadata';
import { type ContentOptionValue, useConfigPanelOptions } from './config-panel-options';
import { ConfigPanel } from './ConfigPanel';
import {
	arePanelConfigurationsEqual,
	type ManagedBlockConfigSaveHandler,
	type ManagedBlockConfiguration,
	type ManagedBlockConfigurationUpdate,
	getDefaultStructuredPanelConfiguration,
	getOutputFormatForGoal,
	hasPersistedConfigurationChanged,
	toAiManagedObjectConfigInput,
} from './configuration';
import { GoalSelect } from './GoalSelect';
import { ManagedBlockConfigFrame } from './ManagedBlockConfigFrame';
import { PromptPanel } from './PromptPanel';
import type { SmartCardProviderProduct } from './sources/smart-card-product';

type SourceValidationError = 'invalid' | 'pending' | 'required' | null;

const saveErrorStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	color: token('color.text.danger'),
});

const footerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const actionsStyles = css({
	marginLeft: 'auto',
});

const TEXT_ENTRY_INPUT_TYPES: ReadonlySet<string> = new Set([
	'email',
	'number',
	'password',
	'search',
	'tel',
	'text',
	'url',
]);

type OwnerWindow = Window & typeof globalThis;

const isElementInWindow = (
	target: EventTarget | null,
	ownerWindow: OwnerWindow,
): target is HTMLElement => target instanceof ownerWindow.HTMLElement;

const isTextEntryTarget = (
	target: EventTarget | null,
	panelElement: HTMLElement,
	ownerWindow: OwnerWindow,
): boolean => {
	if (!isElementInWindow(target, ownerWindow)) {
		return false;
	}

	const element = target.closest('input, textarea, [contenteditable="true"], [role="textbox"]');
	if (!isElementInWindow(element, ownerWindow) || !panelElement.contains(element)) {
		return false;
	}

	if (element instanceof ownerWindow.HTMLTextAreaElement) {
		return true;
	}

	if (element instanceof ownerWindow.HTMLInputElement) {
		return TEXT_ENTRY_INPUT_TYPES.has(element.type);
	}

	return element.isContentEditable || element.getAttribute('role') === 'textbox';
};

export type ManagedBlockConfigProps = {
	/** Hydrated AI-managed object metadata for the configured block. */
	aimoData?: AiManagedObject | null;
	/** Site cloud ID, used for profilecard hydration. */
	cloudId?: string;
	/** Current Confluence page URL to use for current-page sources. */
	currentPageSourceUrl?: string;
	/** If set, an error message to display inline at the bottom of the panel. */
	error?: string | null;
	/** Initial configuration hydrated from the persisted AI-managed object. */
	initialConfiguration: ManagedBlockConfiguration;
	/** If true, the panel is currently performing an async operation. */
	isSaving?: boolean;
	/** If false, keyboard shortcut listeners are disabled for hidden panel instances. */
	isVisible?: boolean;
	/** Called when the user dismisses the panel. */
	onCancel?: () => void;
	/** Called when the user changes config after an error is shown. */
	onClearError?: () => void;
	/** Called when the user clicks save with normalized AIMO config input. */
	onSave: ManagedBlockConfigSaveHandler;
	/** Persisted configuration used to disable no-change saves. */
	persistedConfig?: AiManagedObjectConfig | null;
	/** If true, show the source disclaimer immediately when the panel first renders. */
	showInitialSourceDisclaimer?: boolean;
	/** Product context supplied to Smart Links when rendering configured source chips. */
	smartCardProviderProduct?: SmartCardProviderProduct;
};

/**
 * Inline "Configure block" panel rendered directly underneath an
 * agent-managed bodied extension.
 */
export function ManagedBlockConfig({
	aimoData = null,
	cloudId,
	currentPageSourceUrl,
	initialConfiguration,
	isVisible = true,
	onSave,
	onCancel,
	onClearError,
	isSaving = false,
	error = null,
	persistedConfig = null,
	smartCardProviderProduct,
	showInitialSourceDisclaimer = false,
}: ManagedBlockConfigProps): JSX.Element {
	const intl = useIntl();
	const { goalOptions: promptGoalOptions } = useConfigPanelOptions();
	const [configuration, setConfiguration] =
		useState<ManagedBlockConfiguration>(initialConfiguration);
	const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
	const hasEditedConfigurationRef = useRef(false);
	const hasEditedPromptRef = useRef(false);
	const lastHydratedConfigurationRef = useRef(initialConfiguration);
	const panelRef = useRef<HTMLDivElement>(null);
	const hadFocusWithinPanelRef = useRef(false);
	const hasFocusedPanelRef = useRef(false);
	const configurationRef = useRef(configuration);

	useEffect(() => {
		configurationRef.current = configuration;
	}, [configuration]);

	useEffect(() => {
		const currentConfiguration = configurationRef.current;
		const lastHydratedConfiguration = lastHydratedConfigurationRef.current;
		lastHydratedConfigurationRef.current = initialConfiguration;

		if (
			arePanelConfigurationsEqual(
				currentConfiguration,
				lastHydratedConfiguration,
				currentPageSourceUrl,
			)
		) {
			hasEditedConfigurationRef.current = false;
			hasEditedPromptRef.current = false;
			setConfiguration(initialConfiguration);
		}
	}, [currentPageSourceUrl, initialConfiguration]);

	useEffect(() => {
		if (!isVisible) {
			hasFocusedPanelRef.current = false;
			return;
		}

		if (hasFocusedPanelRef.current) {
			return;
		}

		const panelElement = panelRef.current;
		if (!panelElement) {
			return;
		}

		panelElement.focus({ preventScroll: true });
		hadFocusWithinPanelRef.current = true;
		hasFocusedPanelRef.current = true;
	}, [isVisible]);

	const clearErrorsOnInteraction = useCallback(() => {
		setHasAttemptedSave(false);
		onClearError?.();
	}, [onClearError]);

	const markConfigurationEdited = useCallback(() => {
		hasEditedConfigurationRef.current = true;
	}, []);

	const handleConfigurationChange = useCallback((update: ManagedBlockConfigurationUpdate) => {
		setConfiguration(update);
	}, []);

	const handlePromptToggle = useCallback(() => {
		const currentMode = configurationRef.current.mode;
		const shouldResetConfiguration = currentMode === 'prompt' && hasEditedPromptRef.current;
		const shouldResetPrompt = currentMode === 'configuration' && hasEditedConfigurationRef.current;

		clearErrorsOnInteraction();
		setConfiguration((currentConfiguration) => {
			if (currentConfiguration.mode === 'prompt') {
				if (shouldResetConfiguration) {
					return getDefaultStructuredPanelConfiguration(currentConfiguration);
				}

				return {
					...currentConfiguration,
					mode: 'configuration',
				};
			}

			if (shouldResetPrompt) {
				return {
					...currentConfiguration,
					mode: 'prompt',
					prompt: '',
				};
			}

			return {
				...currentConfiguration,
				mode: 'prompt',
			};
		});

		if (shouldResetConfiguration || shouldResetPrompt) {
			hasEditedConfigurationRef.current = false;
			hasEditedPromptRef.current = false;
		}
	}, [clearErrorsOnInteraction]);

	const handlePromptChange = useCallback(
		(nextPrompt: string) => {
			clearErrorsOnInteraction();

			if (configurationRef.current.prompt !== nextPrompt) {
				hasEditedPromptRef.current = true;
			}

			setConfiguration((currentConfiguration) => ({
				...currentConfiguration,
				prompt: nextPrompt,
			}));
		},
		[clearErrorsOnInteraction],
	);

	const handlePromptContentSelectionChange = useCallback(
		(nextSelection: ContentOptionValue) => {
			if (nextSelection === 'prompt') {
				return;
			}

			const shouldResetConfiguration =
				configurationRef.current.mode === 'prompt' && hasEditedPromptRef.current;

			clearErrorsOnInteraction();

			setConfiguration((currentConfiguration) => {
				const baseConfiguration = shouldResetConfiguration
					? getDefaultStructuredPanelConfiguration(currentConfiguration)
					: {
							...currentConfiguration,
							mode: 'configuration' as const,
						};
				const nextOutputFormat = getOutputFormatForGoal(
					nextSelection,
					baseConfiguration.outputFormat,
				);

				if (
					baseConfiguration.goal !== nextSelection ||
					baseConfiguration.outputFormat !== nextOutputFormat
				) {
					hasEditedConfigurationRef.current = true;
				}

				return {
					...baseConfiguration,
					goal: nextSelection,
					outputFormat: nextOutputFormat,
				};
			});

			if (shouldResetConfiguration) {
				hasEditedPromptRef.current = false;
			}
		},
		[clearErrorsOnInteraction],
	);

	const configInput = useMemo(
		() => toAiManagedObjectConfigInput(configuration, currentPageSourceUrl),
		[configuration, currentPageSourceUrl],
	);
	const validation = useMemo(() => validateManagedBlockConfig(configInput), [configInput]);
	const manualSourceValidationError = useMemo<SourceValidationError>(() => {
		if (configuration.mode !== 'configuration' || configuration.sourceMode !== 'manual') {
			return null;
		}

		if (configuration.sourceEntries.length === 0) {
			return 'required';
		}

		if (configuration.sourceEntries.some((source) => source.status === 'resolving')) {
			return 'pending';
		}

		if (configuration.sourceEntries.some((source) => source.status === 'invalid')) {
			return 'invalid';
		}

		if (!configuration.sourceEntries.some((source) => source.status === 'resolved')) {
			return 'required';
		}

		return null;
	}, [configuration.mode, configuration.sourceEntries, configuration.sourceMode]);
	const sourceValidationError =
		hasAttemptedSave &&
		configuration.mode === 'configuration' &&
		configuration.sourceMode === 'manual'
			? manualSourceValidationError
			: null;
	const isAdditionalContextInvalid =
		configuration.mode === 'configuration' &&
		configuration.isAdditionalContextSelected &&
		configuration.additionalContext.trim().length === 0;
	const additionalContextValidationError = hasAttemptedSave && isAdditionalContextInvalid;
	const promptValidationError =
		hasAttemptedSave && configuration.mode === 'prompt' && configuration.prompt.trim().length === 0;
	const hasConfigurationChanged = hasPersistedConfigurationChanged(persistedConfig, configInput);
	const isSaveDisabled = isSaving || !hasConfigurationChanged;

	const handleSave = useCallback(() => {
		if (isSaveDisabled) {
			return;
		}

		setHasAttemptedSave(true);

		if (manualSourceValidationError || isAdditionalContextInvalid || !validation.valid) {
			return;
		}

		void onSave(configInput);
	}, [
		configInput,
		isAdditionalContextInvalid,
		isSaveDisabled,
		manualSourceValidationError,
		onSave,
		validation,
	]);

	const handleCancel = useCallback(() => {
		onCancel?.();
	}, [onCancel]);

	useEffect(() => {
		if (!isVisible) {
			return;
		}

		const panelElement = panelRef.current;
		const ownerWindow = panelElement?.ownerDocument.defaultView as OwnerWindow | null;
		if (!panelElement || !ownerWindow) {
			return;
		}

		const stopShortcutPropagation = (event: KeyboardEvent, shouldPreventDefault = false) => {
			if (shouldPreventDefault) {
				event.preventDefault();
			}
			event.stopPropagation();
			event.stopImmediatePropagation();
		};

		const isTargetInsidePanel = (target: EventTarget | null): boolean =>
			target instanceof ownerWindow.Node && panelElement.contains(target);

		const handleFocusIn = (event: FocusEvent): void => {
			hadFocusWithinPanelRef.current = isTargetInsidePanel(event.target);
		};

		const handlePointerDown = (event: PointerEvent): void => {
			hadFocusWithinPanelRef.current = isTargetInsidePanel(event.target);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			const eventTarget = event.target;
			const isInsidePanel = isTargetInsidePanel(eventTarget);
			const isDocumentFocusFallback =
				hadFocusWithinPanelRef.current &&
				ownerWindow.document.activeElement === ownerWindow.document.body &&
				(eventTarget === ownerWindow.document.body || eventTarget === ownerWindow.document);

			if (!isInsidePanel && !isDocumentFocusFallback) {
				return;
			}

			const isCloseShortcut = event.key === 'Escape' || event.key === 'Esc';
			const isTextEntry =
				isInsidePanel && isTextEntryTarget(eventTarget, panelElement, ownerWindow);
			const isModifiedEnter = event.key === 'Enter' && (event.metaKey || event.ctrlKey);
			const isPlainEnter =
				event.key === 'Enter' &&
				!event.altKey &&
				!event.ctrlKey &&
				!event.metaKey &&
				!event.shiftKey;
			const isSaveShortcut = isTextEntry ? isModifiedEnter : isPlainEnter;
			const shouldSuppressModifiedEnter = !isTextEntry && isModifiedEnter;

			if (!isSaveShortcut && !isCloseShortcut && !shouldSuppressModifiedEnter) {
				return;
			}

			if (isCloseShortcut) {
				stopShortcutPropagation(event);
				if (!isSaving) {
					handleCancel();
				}
				return;
			}

			if (shouldSuppressModifiedEnter) {
				stopShortcutPropagation(event);
				return;
			}

			stopShortcutPropagation(event, true);
			handleSave();
		};

		ownerWindow.addEventListener('focusin', handleFocusIn, true);
		ownerWindow.addEventListener('pointerdown', handlePointerDown, true);
		ownerWindow.addEventListener('keydown', handleKeyDown, true);

		return () => {
			ownerWindow.removeEventListener('focusin', handleFocusIn, true);
			ownerWindow.removeEventListener('pointerdown', handlePointerDown, true);
			ownerWindow.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [handleCancel, handleSave, isSaving, isVisible]);

	return (
		<ManagedBlockConfigFrame ref={panelRef} testId="agent-managed-extension-configure-panel">
			{configuration.mode === 'configuration' ? (
				<ConfigPanel
					additionalContextValidationError={additionalContextValidationError}
					configuration={configuration}
					isDisabled={isSaving}
					onChange={handleConfigurationChange}
					onConfigEdit={markConfigurationEdited}
					onInteraction={clearErrorsOnInteraction}
					onPromptSelect={handlePromptToggle}
					showInitialSourceDisclaimer={showInitialSourceDisclaimer}
					smartCardProviderProduct={smartCardProviderProduct}
					sourceValidationError={sourceValidationError}
				/>
			) : (
				<Stack space="space.150">
					<GoalSelect
						contentSelection="prompt"
						isDisabled={isSaving}
						onContentSelectionChange={handlePromptContentSelectionChange}
						options={promptGoalOptions}
					/>
					<PromptPanel
						isDisabled={isSaving}
						isExpanded
						onPromptChange={handlePromptChange}
						prompt={configuration.prompt}
						promptValidationError={promptValidationError}
						showToggle={false}
					/>
				</Stack>
			)}

			{error && (
				<div css={saveErrorStyles} data-testid="agent-managed-extension-error" role="alert">
					<StatusErrorIcon label="" color="currentColor" size="small" />
					<Text color="color.text.danger">{error}</Text>
				</div>
			)}
			<div css={footerStyles}>
				<AimoStatusMetadata aimoData={aimoData} cloudId={cloudId} />
				<div css={actionsStyles}>
					<Inline space="space.100" alignInline="end">
						{onCancel && (
							<Button appearance="subtle" onClick={handleCancel} isDisabled={isSaving}>
								{intl.formatMessage(messages.cancel)}
							</Button>
						)}
						<Button
							appearance="primary"
							testId="agent-managed-extension-save"
							onClick={handleSave}
							isDisabled={isSaveDisabled}
							iconAfter={isSaving ? () => <Spinner size="small" /> : undefined}
						>
							{intl.formatMessage(messages.save)}
						</Button>
					</Inline>
				</div>
			</div>
		</ManagedBlockConfigFrame>
	);
}
